import { useQuery } from '@tanstack/react-query';
import { differenceInCalendarDays, format, subDays } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { DateRangeState } from '@/Domain/types/DateRange.types';

// Types
interface Statistics {
  conversation_stats?: {
    conversations?: number;
    conversations_last_period?: number;
    conversations_handoff?: number;
    conversations_handoff_last_period?: number;
    conversations_by_day?: Record<string, number>;
    conversations_by_weekday?: Record<string, number>;
    conversations_by_hour?: Record<string, number>;
    conversations_by_source?: Record<string, number>;
    conversations_by_language?: Record<string, number>;
    conversations_by_messages?: Record<string, number>;
    conversations_by_format?: Record<string, number>;
    handoff_conversations_by_format?: Record<string, number>;
    conversations_by_duration?: {
      duration?: Record<string, number>;
      durations?: Record<string, number>;
      cutoff?: number;
      outliers?: number;
    };
  };
  message_stats?: {
    messages?: number;
    avg_messages_per_conversation?: number;
    avg_messages_per_conversation_last_period?: number;
    messages_by_day?: Record<string, number>;
  };
  // Root-level billing fields
  messages_billing_period?: number;
  messages_limit_billing_period?: number;
}

// Helper functions
const getPercentChange = (current: number, previous: number): number => {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  return ((current - previous) / previous) * 100;
};

const usePercentageMemo = (value: number, comparisonCurrent: number, comparisonPrevious: number) =>
  useMemo(
    () => [value, getPercentChange(comparisonCurrent, comparisonPrevious)],
    [value, comparisonCurrent, comparisonPrevious],
  );

// Local ISO date helper to match queryClient expectations (yyyy-MM-dd)
const toISODate = (date?: Date) => (date ? format(date, 'yyyy-MM-dd') : '');

// API day boundaries are UTC, so read the calendar date in UTC but keep it a local-calendar Date to match toISODate.
const utcCalendarDay = (instant: Date) =>
  new Date(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate());

export const getCompletedComparisonRange = (
  range: DateRangeState,
  now = new Date(),
): DateRangeState => {
  const latestCompleteDay = subDays(utcCalendarDay(now), 1);
  const daysToShift = Math.max(0, differenceInCalendarDays(range.endDate, latestCompleteDay));
  if (daysToShift === 0) return range;

  return {
    startDate: subDays(range.startDate, daysToShift),
    endDate: subDays(range.endDate, daysToShift),
  };
};

export const useAnalyticsInsights = (range: DateRangeState) => {
  const { t } = useTranslation(['insights_page', 'dashboard', 'common']);

  // Fetch statistics for selected date range using existing query key
  const { data, isLoading } = useQuery<Statistics>({
    queryKey: [
      QueryKey.analytics.general,
      { start: toISODate(range.startDate), end: toISODate(range.endDate) },
    ],
  });

  const comparisonRange = useMemo(() => getCompletedComparisonRange(range), [range]);
  const { data: comparisonData, isLoading: isComparisonLoading } = useQuery<Statistics>({
    queryKey: [
      QueryKey.analytics.general,
      { start: toISODate(comparisonRange.startDate), end: toISODate(comparisonRange.endDate) },
    ],
  });
  // Gate on data, not isError: a paused (offline) query is neither loading nor errored.
  // Both windows, because a real trend beside a headline defaulted to 0 reads as a real 0.
  const hasComparison = data !== undefined && comparisonData !== undefined;

  // KPI calculations
  const conversationsNow = data?.conversation_stats?.conversations ?? 0;
  const conversationsComparisonNow = comparisonData?.conversation_stats?.conversations ?? 0;
  const conversationsPrev = comparisonData?.conversation_stats?.conversations_last_period ?? 0;
  const [conversations, conversationsPercentChange] = usePercentageMemo(
    conversationsNow,
    conversationsComparisonNow,
    conversationsPrev,
  );

  const messagesNow = data?.message_stats?.avg_messages_per_conversation ?? 0;
  const messagesComparisonNow = comparisonData?.message_stats?.avg_messages_per_conversation ?? 0;
  const messagesPrev =
    comparisonData?.message_stats?.avg_messages_per_conversation_last_period ?? 0;
  const [messages, messagesPercentChange] = usePercentageMemo(
    messagesNow,
    messagesComparisonNow,
    messagesPrev,
  );

  // Human handoff as NUMBER (not rate)
  const handoffNow = data?.conversation_stats?.conversations_handoff ?? 0;
  const handoffComparisonNow = comparisonData?.conversation_stats?.conversations_handoff ?? 0;
  const handoffPrev = comparisonData?.conversation_stats?.conversations_handoff_last_period ?? 0;
  const handoffPercentChange = useMemo(
    () => getPercentChange(handoffComparisonNow, handoffPrev),
    [handoffComparisonNow, handoffPrev],
  );

  // Human handoff RATE (percentage of total conversations)
  const handoffRateNow =
    conversationsComparisonNow > 0 ? (handoffComparisonNow / conversationsComparisonNow) * 100 : 0;
  const handoffRatePrev = conversationsPrev > 0 ? (handoffPrev / conversationsPrev) * 100 : 0;
  // Calculate percentage point difference (not relative change)
  const handoffRatePercentChange = useMemo(
    () => handoffRateNow - handoffRatePrev,
    [handoffRateNow, handoffRatePrev],
  );

  // New KPI data
  const conversationsByFormat = data?.conversation_stats?.conversations_by_format ?? {};
  const handoffConversationsByFormat =
    data?.conversation_stats?.handoff_conversations_by_format ?? {};
  const totalMessages = data?.message_stats?.messages ?? 0;
  const avgMessagesPerConversation = data?.message_stats?.avg_messages_per_conversation ?? 0;
  const messagesBillingPeriod = data?.messages_billing_period ?? 0;
  const messagesLimitBillingPeriod = data?.messages_limit_billing_period ?? 0;

  // Chart data preparations
  const conversationsByDay = data?.conversation_stats?.conversations_by_day ?? {};
  const messagesByDay = data?.message_stats?.messages_by_day ?? {};
  const conversationsByWeekday = data?.conversation_stats?.conversations_by_weekday ?? {};
  const conversationsByHour = data?.conversation_stats?.conversations_by_hour ?? {};
  const conversationsBySource = data?.conversation_stats?.conversations_by_source ?? {};
  const conversationsByLanguage = data?.conversation_stats?.conversations_by_language ?? {};
  const conversationsByMessages = data?.conversation_stats?.conversations_by_messages ?? {};

  const convDuration = data?.conversation_stats?.conversations_by_duration;
  const durationBuckets = (convDuration?.duration ??
    (convDuration as any)?.durations ??
    {}) as Record<string, number>;
  const durationCutoff = Number(convDuration?.cutoff ?? 0);
  const durationOutliers = Number(convDuration?.outliers ?? 0);

  return {
    // Loading state
    isLoading,
    isComparisonLoading,
    hasComparison,

    // KPIs
    conversations,
    conversationsPercentChange,
    conversationsByFormat,
    totalMessages,
    avgMessagesPerConversation,
    messagesBillingPeriod,
    messagesLimitBillingPeriod,
    messages,
    messagesPercentChange,
    handoffNow,
    handoffPercentChange,
    handoffRatePercentChange,
    handoffConversationsByFormat,

    // Chart data
    conversationsByDay,
    messagesByDay,
    conversationsByWeekday,
    conversationsByHour,
    conversationsBySource,
    conversationsByLanguage,
    conversationsByMessages,
    durationBuckets,
    durationCutoff,
    durationOutliers,

    // Translation helper
    t,
  };
};
